import { abs } from "@suse-o11y/abs";
import type { NonEmptyArray } from "@suse-o11y/non-empty-array";

import {
	addDays,
	differenceInDays,
	endOfDay,
	endOfYear,
	format,
	isSameDay,
	isSameYear,
	isYesterday,
	startOfDay,
	startOfYear,
	subDays,
} from "../date-fns";
import { isBeforeToday } from "../is-before-today";
import { isWithinOpenInterval } from "../open-interval";
import { startOfNextDay } from "../start-of-next-day";
import { startOfNextYear } from "../start-of-next-year";
import type { Time } from "../time";

import type { DateTimeFormat } from "./date-time-format";
import type { DateTimePattern } from "./date-time-pattern";
import type { RelativeTime } from "./relative-time";

/**
 * TODO STAC-22104 clean up from unused properties
 */
interface RelativeDateFormat {
	/**
	 * @deprecated used only in tests, can be deleted
	 */
	name: string;

	/**
	 * @deprecated not used outside of getRelativeDateTimeFormat, so it can define format instead
	 */
	pattern: DateTimePattern;

	/**
	 * Relative times are ordered depending on the delta between the given time and now. The greater the delta
	 * the more information needs to be given about the given.
	 * For example, if the given time is just an hour ago you might not need to name the day, but if the given time
	 * is a week ago you might not need to name the moth, etc.
	 */
	order: number;

	/**
	 * If this format can be used as a relative time format this function will return the time until relative formatting is possible.
	 *
	 */
	canShowAsRelativeTime: (time: Time, now: Time, viewTime?: Time) => undefined | RelativeTime;
}

export const Today = {
	name: "Today",
	order: 0,
	pattern: {
		unicode: "'Today'",
		g2: "[Today]",
	},

	canShowAsRelativeTime: (time, now, viewTime) =>
		isSameDay(time, viewTime ?? now, now)
			? {
					expiration: startOfNextDay(now),
					range: [startOfDay(now), endOfDay(now)],
				}
			: undefined,
} satisfies RelativeDateFormat;

export const Yesterday = {
	name: "Yesterday",
	order: 0,
	pattern: {
		unicode: "'Yesterday'",
		g2: "[Yesterday]",
	},
	canShowAsRelativeTime: (time, now, viewTime) =>
		isSameDay(now, viewTime ?? now) && isYesterday(time, { now })
			? {
					expiration: startOfNextDay(now),
					range: [startOfDay(subDays(now, 1)), startOfDay(now)],
				}
			: undefined,
} satisfies RelativeDateFormat;

export const Weekday = {
	name: "Weekday",
	order: 1,
	pattern: {
		unicode: "'Last' EEE", // e.g. "Last Mon",
		g2: "[Last] ddd", // e.g. "Last Mon",
	},
	canShowAsRelativeTime: (time, now, viewTime) =>
		isSameDay(now, viewTime ?? now) && abs(differenceInDays(time, now)) <= 6 && isBeforeToday(time, { now })
			? {
					expiration: startOfDay(addDays(time, 8)), // e.g. if it is Friday and you are referring to Last Friday, you can refer to it until the end of the day
					range: [startOfDay(subDays(now, 7)), startOfDay(now)], // can refer to "Last ddd" until Yesterday.
				}
			: undefined,
} satisfies RelativeDateFormat;

export const DayMonth = {
	name: "DayMonth",
	order: 2,
	pattern: {
		unicode: "MMM d",
		g2: "MMM D",
	},
	canShowAsRelativeTime: (time, now, viewTime) =>
		isSameYear(time, viewTime ?? now, now)
			? {
					expiration: startOfNextYear(time),
					range: [startOfYear(time), endOfYear(time)],
				}
			: undefined,
} satisfies RelativeDateFormat;

const ALL_RELATIVE_DATE_FORMATS = [Today, Yesterday, Weekday, DayMonth];

/**
 * Finds a suitable date format based on the following rules:
 * - times are relative compared to viewTime and now. We can hide information if the same info applies to both viewTime and now
 *   (e.g. viewTime and now are the same day, same week, etc.)
 * - if multiple times are given the most coarse grained one is used, so as not to hide vital information.
 * - some format are disqualified when multiple times are given, because they are not adaptive to formatting (like today and yesterday)
 * - if no format is found, the absolute date format is used
 *
 * Current rules:
 * --
 * When view time and current time are the same day and time is today, say Today.
 * else when view time and current time are the same day and time is yesterday, say Yesterday.
 * else when view time and current time are the same week and time is within the same week, refer to last X (e.g. last Tuesday)
 * else when view time and current time are the same year and time is the same year, name month and day
 * else use absolute date formatting
 */
export function getRelativeDateFormat(
	{ viewTime, now }: { viewTime?: Time; now: Time },
	...times: NonEmptyArray<Time>
): DateTimeFormat {
	const qualifiedFormats = times.flatMap((time) => {
		for (const formatDef of ALL_RELATIVE_DATE_FORMATS) {
			const relativeTime = formatDef.canShowAsRelativeTime(time, now, viewTime);
			if (relativeTime && times.every((t) => isWithinOpenInterval(t, relativeTime.range))) {
				return [
					{
						...formatDef,
						relative: relativeTime,
						format: (value: Time) => format(value, formatDef.pattern.unicode),
					},
				];
			}
		}
		return [];
	});

	if (qualifiedFormats.length === 0) {
		return ABSOLUTE_DATE_FORMAT;
	}

	// return the formatting with the higest order, i.e. most coarse grained
	return qualifiedFormats.reduce((acc, a) => (a.order < acc.order ? a : acc), qualifiedFormats[0]);
}

export const ABSOLUTE_DATE_FORMAT = {
	name: "DayMonthYear",
	pattern: {
		unicode: "MMM d, yyyy",
		g2: "MMM D, YYYY",
	},
	format: (time) => format(time, "MMM d, yyyy"),
} satisfies DateTimeFormat;
